import {
	type MetaFunction,
	type HeadersFunction,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	json,
	defer,
} from "@remix-run/node";
import {
	isRouteErrorResponse,
	useLoaderData,
	useRouteError,
} from "@remix-run/react";

import type * as typo3 from "~/modules/typo3/schema";
import ErrorLayout from "~/components/layouts/ErrorLayout";
import { renderLayoutComponent } from "~/modules/typo3/backend-layouts";
import { mergeHeaders } from "~/utils/headers";
import { findSinglePlugin } from "~/components/layouts/PageLayout";
import { getAllChildContentElementsForResponse } from "~/utils/typo3";
import { getVimeoVideoIdFromUrl } from "~/utils/vimeo";
import { isNonNullable } from "~/utils/nonNullable";
import { makeVimeoVideoDataQuery, queryClient } from "~/data";
import {
	buildTypo3Url,
	fetchHeadlessJSON,
	getTypo3ApiUrl,
	responseToHeadlessJSON,
} from "~/modules/typo3/fetch";
import { calendarDataSchema, responseSchema } from "~/modules/typo3/schema";
import { serverCap } from "~/modules/cap/server";
import { actionResponseSchema } from "~/modules/typo3/form";

async function fetchEventsData(path: string, request: Request) {
	const eventsUrl = buildTypo3Url(path, request);
	eventsUrl.searchParams.set("type", "6833");

	let response;
	try {
		response = await fetchHeadlessJSON(eventsUrl, {
			headers: request.headers,
		});
	} catch (error) {
		// do not log here, as we are already logging in `fetchHeadlessJSON`
		return null;
	}

	if (response instanceof Response) {
		// TODO: handle response data (this case should not occur, Response is only
		// emmited for redirects - without data)
		return null;
	}

	try {
		const parsed = await calendarDataSchema.safeParseAsync(response.data);
		if (!parsed.success) {
			// Must return a defined value: `defer()` rejects a deferred key that
			// resolves with `undefined`.
			return null;
		}
		return parsed.data;
	} catch (err) {
		console.error(`Failed to process events data (${eventsUrl})`, err);
		return null;
	}
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const start = Date.now();
	const path = params["*"] ?? "/";
	const eventsData = fetchEventsData(path, request);

	const response = await fetchHeadlessJSON(buildTypo3Url(path, request), {
		headers: request.headers,
	});
	if (response instanceof Response) {
		throw response;
	}

	const validated = await responseSchema.safeParseAsync(response.data);
	if (!validated.success) {
		console.error(
			"Schema error in TYPO3 response (Loader)",
			validated.error,
			JSON.stringify(response.data),
		);
		throw new Response("Internal error (Schema)", {
			status: 500,
		});
	}

	const vimeoVideoData = fetchVimeoVideoData(validated.data);

	const st = response.headers["server-timing"];

	return defer(
		{
			pageData: validated.data,
			eventsData,
			vimeoVideoData,
		},
		{
			headers: {
				...response.headers,
				"server-timing": `${st ? `${st}, ` : ""}pageloader;dur=${Date.now() - start}`,
				"x-page-loader-duration": `${Date.now() - start}`,
			},
		},
	);
}

function extractVideoIdsFromMedias(medias: Array<typo3.Media>) {
	return medias
		.map((media) =>
			media.type === "video"
				? getVimeoVideoIdFromUrl(media.properties.originalUrl)
				: undefined,
		)
		.filter(isNonNullable);
}

function extractVideoIdsFromContent(res: typo3.Response) {
	const videoIds = getAllChildContentElementsForResponse(res)
		.flatMap((ce) => {
			switch (ce.type) {
				case "video":
					return extractVideoIdsFromMedias(ce.content.video);

				case "nnbuhaarticlesandpersons_mediaquartet":
				case "nnbuhaarticlesandpersons_media":
				case "nnbuhaarticlesandpersons_podcaststripe":
					return ce.content.data.articles.flatMap(
						({ relatedMedia }) =>
							extractVideoIdsFromMedias(relatedMedia),
					);
			}

			if (
				"content" in ce &&
				"relatedMedia" in ce.content &&
				Array.isArray(ce.content.relatedMedia)
			) {
				return extractVideoIdsFromMedias(ce.content.relatedMedia);
			}
		})
		.filter(isNonNullable);
	return new Set(videoIds);
}

async function fetchVimeoVideoData(res: typo3.Response) {
	const videoIds = extractVideoIdsFromContent(res);
	const queries = Array.from(videoIds.values()).map(async (id) => {
		try {
			const data = await queryClient.fetchQuery(
				makeVimeoVideoDataQuery(id),
			);
			return [id, data] as const;
		} catch (err) {
			console.warn(`Unable to fetch data for vimeo video "${id}"`, err);
			return null;
		}
	});
	const dataEntries = await Promise.all(queries);
	return Object.fromEntries(dataEntries.filter(isNonNullable));
}

export const headers: HeadersFunction = ({ loaderHeaders, parentHeaders }) => {
	const passedParentHeaders = ["content-security-policy"];

	const passedLoaderHeaders = [
		"cache-control",
		"x-typo3-parsetime",
		"server-timing",
	];

	return mergeHeaders(
		{},

		Object.fromEntries(
			passedParentHeaders.map(
				(headerName) =>
					[headerName, parentHeaders.get(headerName)] as const,
			),
		),

		Object.fromEntries(
			passedLoaderHeaders.map(
				(headerName) =>
					[headerName, loaderHeaders.get(headerName)] as const,
			),
		),
	);
};

export async function action({ request }: ActionFunctionArgs) {
	const url = new URL(request.url);
	const searchParams = new URLSearchParams(url.search);

	const body = await request.formData();
	const capToken = body.get("capToken");

	const isFormRequest = searchParams.has("headlessFormUid");

	if (isFormRequest) {
		try {
			if (capToken === null) {
				throw new Error("Cap token not set");
			}

			const isCapTokenValid = (
				await serverCap.validateToken(capToken.toString())
			).success;
			if (!isCapTokenValid) {
				throw new Error(`Cap token is not valid - ${capToken}`);
			}
		} catch (err) {
			console.error(
				"Error processing Form Cap token",
				JSON.stringify(err),
			);

			throw new Response(`Invalid Cap - ${String(err)}`, {
				status: 400,
				headers: {
					"content-type": "text/plain",
				},
			});
		}
	}

	const response = await fetchHeadlessJSON(
		getTypo3ApiUrl(url.pathname + url.search),
		{
			headers: request.headers,
			method: request.method,
			body,
		},
	);

	if (isFormRequest) {
		try {
			const { data } = await actionResponseSchema.safeParseAsync(
				await responseToHeadlessJSON(response),
			);
			if (
				data !== undefined &&
				data.api.page.type === "Result" &&
				capToken !== null
			) {
				await serverCap.removeToken(capToken.toString());
			}
		} catch (err) {
			console.error(
				"Error processing Form Cap token",
				JSON.stringify(err),
			);
		}
	}

	if (response instanceof Response) {
		throw response;
	}

	//const validated = await responseSchema.safeParseAsync(data);
	//if (!validated.success) {
	//	console.error(
	//		"Schema error in TYPO3 response (Action)",
	//		validated.error,
	//	);
	//	throw new Response("Internal Error (Schema)", {
	//		status: 500,
	//	});
	//}

	return json(response.data, {
		status: response.status,
		headers: mergeHeaders(response.headers, {
			"cache-control": "no-store, max-age=0",
		}),
	});
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	if (!data) return [];

	const { meta } = data.pageData;

	const singlePlugin = findSinglePlugin({ page: data.pageData });

	const pageTitle =
		data.pageData.id === 1
			? "Burg Giebichenstein Kunsthochschule Halle"
			: (singlePlugin?.meta.title ?? meta.title) +
				" - Burg Giebichenstein Kunsthochschule Halle";

	return [
		{ title: pageTitle },
		{
			name: "description",
			content: singlePlugin?.meta.description ?? meta.description,
		},
		{
			name: "keywords",
			content: singlePlugin?.meta.keywords ?? meta.keywords,
		},
		{
			name: "robots",
			content: [
				meta.robots.noIndex ? "noindex" : "index",
				meta.robots.noFollow ? "nofollow" : "follow",
			].join(", "),
		},

		{
			property: "og:title",
			content: meta.ogTitle || pageTitle,
		},
		{
			property: "og:description",
			content: singlePlugin?.meta.description ?? meta.ogDescription,
		},

		{
			property: "twitter:card",
			content: meta.twitterCard,
		},
		{
			property: "twitter:title",
			content: meta.twitterTitle || pageTitle,
		},
		{
			property: "twitter:description",
			content: singlePlugin?.meta.description ?? meta.twitterDescription,
		},
		/*
		 * TODO:
		 * 	canonical: z.string(), //typo3LinkSchema,
		 * 	author: z.string(),
		 * 	authorEmail: z.string(),
		 * 	twitterImage: z.object({}).nullable(),
		 * 	schema: z.string().optional(),
		 * { "og:image": data.meta.ogImage },  // z.object({}).nullable(), // TODO shape
		 */
	];
};

export function ErrorBoundary() {
	const error = useRouteError();

	// when true, this is what used to go to `CatchBoundary`
	if (isRouteErrorResponse(error)) {
		return (
			<ErrorLayout title="Oops">
				<p>Status: {error.status}</p>
				<p>{error.data.message}</p>

				{process.env.NODE_ENV === "development" && (
					<pre className="mt-4">DEV Error: {error.data}</pre>
				)}
			</ErrorLayout>
		);
	}

	return (
		<ErrorLayout title="Uh oh ...">
			<p>Something went wrong.</p>
			<pre>Unknown Error</pre>

			{process.env.NODE_ENV === "development" && (
				<pre className="mt-4">
					DEV Error: {JSON.stringify(error, null, 2)}
				</pre>
			)}
		</ErrorLayout>
	);
}

export default function Catchall() {
	const data = useLoaderData<typeof loader>();
	return renderLayoutComponent(data.pageData);
}
